import { ref, reactive, computed, watch, set as vueSet } from '@vue/composition-api'
import datasetsConfig from '@/data/datasets.json'
import { getDatasetModule } from '../config/datasets'

/**
 * Composable for managing dynamic chart filters.
 *
 * Uses ref({}) with immutable replacements so Vue 2 detects every change
 * (reactive({}) cannot track new property additions).
 *
 * When a dataset has a UI profile, only the filters listed in the profile
 * are returned in `filterKeys` (visible in the UI). All other dataset filters
 * remain available via `setFilter()` for programmatic use.
 *
 * @param {import('@vue/composition-api').Ref<string>} selectedDataset - reactive dataset key
 * @param {Object|null} externalFilters - optional preset filters from props
 * @param {{ selectedField?: import('@vue/composition-api').Ref<string> }} options
 * @returns {Object} filter state, keys, setter, and reset
 */
export function useChartFilters(selectedDataset, externalFilters = null, { selectedField } = {}) {
  const activeFilters = ref({})

  // ─── Process-form mode (kept for internal use, UI toggle removed) ────────
  const processFormMode = ref('count')

  // ─── Count mode: multiple question+answer pairs ─────────────────
  const processFormAnswer = ref('')
  const processFormPairs = ref([{ question: '', answer: '' }])

  // ─── Question-count mode: questions selected for distribution chart ──
  const selectedQuestions = ref([])

  // ─── Analysis mode: per-question answers { questionText: selectedAnswer } ──
  const analysisAnswers = reactive({})

  // ─── All filter keys from datasets.json (exclude 'time') ─────────────
  const allFilterKeys = computed(() => {
    const cfg = datasetsConfig[selectedDataset.value]
    if (!cfg || !cfg.filters) return []
    return Object.keys(cfg.filters).filter(key => key !== 'time')
  })

  // ─── Active process-form label (set by questionnaire selector or auto-set) ──
  const activeProcessFormLabel = ref('')

  // All process-form filters for the current dataset
  const processFormFilters = computed(() => {
    const module = getDatasetModule(selectedDataset.value)
    if (!module?.filters) return []
    return module.filters.filter(f => f.filterType === 'process-form')
  })

  // Whether the current dataset has any process-form filters
  const hasProcessForm = computed(() => processFormFilters.value.length > 0)

  // Whether the current dataset has multiple questionnaires
  const isMultiQuestionnaire = computed(() => processFormFilters.value.length > 1)

  // Questionnaire options for the dropdown (multi-questionnaire mode)
  const questionnaireOptions = computed(() => {
    return processFormFilters.value.map(f => ({
      value: f.forField || f.label,
      label: f.forField || f.label,
    }))
  })

  // Setter for questionnaire selection
  const setActiveProcessFormLabel = (label) => {
    const normalized = label || ''
    // Avoid no-op updates that would trigger filter reload cycles
    if (activeProcessFormLabel.value === normalized) return
    activeProcessFormLabel.value = normalized
    setFilter('question', null)
    processFormAnswer.value = ''
    clearProcessFormPairs()
  }

  // ─── Visible filter keys (scoped by UI profile if available) ─────────
  // Profile filters are trusted as-is: 'where' type filters bypass the dataset
  // filter list because they are packed into the `where` JSON param in useChartUrl.
  // Process-form filters are always included when the dataset has them,
  // regardless of which field is selected.
  const filterKeys = computed(() => {
    const module = getDatasetModule(selectedDataset.value)
    if (!module) return allFilterKeys.value
    const pfLabel = activeProcessFormLabel.value
    return module.filters
      .filter(f => {
        if (f.filterType === 'process-form') {
          // Show process-form filters whenever they exist for the dataset
          return pfLabel && (f.forField || f.label) === pfLabel
        }
        return true
      })
      .map(f => f.key)
  })

  // ─── Apply module-defined defaults (e.g. defaultDays for date filters) ──
  const applyProfileDefaults = () => {
    const module = getDatasetModule(selectedDataset.value)
    if (!module) return
    const defaults = {}
    module.filters.forEach(f => {
      if (f.defaultDays != null) {
        const today = new Date()
        const from = new Date()
        from.setDate(from.getDate() - f.defaultDays)
        const fmt = d => d.toISOString().slice(0, 10)
        defaults[f.key] = `${fmt(from)} to ${fmt(today)}`
      }
    })
    if (Object.keys(defaults).length > 0) {
      activeFilters.value = { ...activeFilters.value, ...defaults }
    }
  }

  // ─── Apply external filters if provided ───────────────
  const applyExternalFilters = (filters) => {
    if (!filters || typeof filters !== 'object') return
    activeFilters.value = { ...activeFilters.value, ...filters }
  }

  // ─── Set a single filter value ────────────────────────
  const setFilter = (key, value) => {
    const current = activeFilters.value[key]
    const isClearing = value === null || value === undefined || value === ''
    // Skip if nothing would actually change — avoids triggering deep watchers needlessly
    if (isClearing && (current === null || current === undefined || !(key in activeFilters.value))) return
    if (!isClearing && current === value) return
    const copy = { ...activeFilters.value }
    if (isClearing) {
      delete copy[key]
    } else {
      copy[key] = value
    }
    activeFilters.value = copy
  }

  // ─── Count-mode answer setter ─────────────────────────
  const setProcessFormAnswer = (answer) => {
    processFormAnswer.value = answer || ''
  }

  // ─── Process-form pairs management ─────────────────────
  const setProcessFormAnswerForQuestion = (question, answer) => {
    if (!question) return
    // Setting an answer removes the question from selectedQuestions (mutually exclusive)
    if (answer) selectedQuestions.value = selectedQuestions.value.filter(q => q !== question)
    let pairs = processFormPairs.value.filter(p => p.question && p.question !== question)
    if (answer) pairs.push({ question, answer })
    if (pairs.length === 0) pairs = [{ question: '', answer: '' }]
    processFormPairs.value = pairs
  }

  const clearProcessFormPairs = () => {
    processFormPairs.value = [{ question: '', answer: '' }]
  }

  // ─── Question-count mode management ─────────────────────
  const toggleSelectedQuestion = (question) => {
    if (!question) return
    const idx = selectedQuestions.value.indexOf(question)
    if (idx === -1) {
      // Adding to count list — remove any filter answer for this question
      setProcessFormAnswerForQuestion(question, null)
      selectedQuestions.value = [...selectedQuestions.value, question]
    } else {
      selectedQuestions.value = selectedQuestions.value.filter(q => q !== question)
    }
  }

  const clearSelectedQuestions = () => {
    selectedQuestions.value = []
  }

  const isQuestionCountMode = computed(() => selectedQuestions.value.length > 0)

  // ─── Analysis answer setters ──────────────────────────
  const setAnalysisAnswer = (question, answer) => {
    if (answer === null || answer === undefined || answer === '') {
      delete analysisAnswers[question]
    } else {
      vueSet(analysisAnswers, question, answer)
    }
  }

  const clearAnalysisAnswers = () => {
    Object.keys(analysisAnswers).forEach(k => delete analysisAnswers[k])
  }

  // ─── Reset all filters ────────────────────────────────
  const resetFilters = ({ preserveTime = false } = {}) => {
    if (preserveTime) {
      const timeFilters = {}
      if (activeFilters.value.from) timeFilters.from = activeFilters.value.from
      if (activeFilters.value.to) timeFilters.to = activeFilters.value.to
      activeFilters.value = timeFilters
    } else {
      activeFilters.value = {}
    }
  }

  // ─── When dataset changes, clear filters and re-apply defaults + external ones
  // Also auto-set activeProcessFormLabel for datasets with process-form filters.
  watch(selectedDataset, () => {
    resetFilters({ preserveTime: true })
    activeProcessFormLabel.value = ''
    selectedQuestions.value = []
    applyProfileDefaults()
    if (externalFilters) {
      applyExternalFilters(externalFilters)
    }

    // Auto-set activeProcessFormLabel for single-questionnaire datasets
    const module = getDatasetModule(selectedDataset.value)
    if (module?.filters) {
      const pfFilters = module.filters.filter(f => f.filterType === 'process-form')
      if (pfFilters.length === 1) {
        activeProcessFormLabel.value = pfFilters[0].forField || pfFilters[0].label
      }
    }
  })

  // ─── When selected field changes, clear question + answer + analysis answers ──
  // Process-form state is NOT cleared here — questions stay visible regardless of field.
  if (selectedField) {
    watch(selectedField, () => {
      setFilter('question', null)
      processFormAnswer.value = ''
      clearProcessFormPairs()
      clearAnalysisAnswers()
      clearSelectedQuestions()
      processFormMode.value = 'count'
    })
  }

  // ─── When question changes, clear the answer ──────────────────────────────
  watch(() => activeFilters.value.question, () => {
    processFormAnswer.value = ''
  })

  // ─── When mode changes, clear the other mode's filters ──
  watch(processFormMode, (mode) => {
    if (mode === 'analysis') {
      setFilter('question', null)
      processFormAnswer.value = ''
      clearProcessFormPairs()
    } else {
      clearAnalysisAnswers()
    }
  })

  // Apply profile defaults and external filters on init
  applyProfileDefaults()
  if (externalFilters) {
    applyExternalFilters(externalFilters)
  }

  // ─── Auto-set on init (covers the case where selectedDataset is already set) ──
  {
    const module = getDatasetModule(selectedDataset.value)
    if (module?.filters) {
      const pfFilters = module.filters.filter(f => f.filterType === 'process-form')
      if (pfFilters.length === 1 && !activeProcessFormLabel.value) {
        activeProcessFormLabel.value = pfFilters[0].forField || pfFilters[0].label
      }
    }
  }

  return {
    activeFilters,
    filterKeys,
    allFilterKeys,
    setFilter,
    resetFilters,
    activeProcessFormLabel,
    processFormFilters,
    hasProcessForm,
    isMultiQuestionnaire,
    questionnaireOptions,
    setActiveProcessFormLabel,
    processFormMode,
    processFormAnswer,
    setProcessFormAnswer,
    processFormPairs,
    setProcessFormAnswerForQuestion,
    clearProcessFormPairs,
    selectedQuestions,
    toggleSelectedQuestion,
    clearSelectedQuestions,
    isQuestionCountMode,
    analysisAnswers,
    setAnalysisAnswer,
    clearAnalysisAnswers,
  }
}
